<template>
  <template v-if="isStandardArtwork && enableAbAiMonetization">
    <CustomNotes
      v-model="note"
      :disabled="disabled"
      :on-learn-more="onLearnMore"
      :index="index"
      @input="saveNote"
    />
  </template>
  <div
    v-else
    class="redraw-notes"
    :class="{
      'redraw-notes--multi-upload': isMultiUpload,
      'redraw-notes--standard-artwork': isStandardArtwork
    }"
  >
    <label :for="`redraw-note-${index}`" class="redraw-notes__label">
      <span class="redraw-notes__label-text">
        <span><strong>Free</strong> optional design editing included</span>
        <span>(a <strong>$10</strong> value)!</span>
      </span>
      <Tooltip
        ref="redrawInformationTooltip"
        :disabled="isMobile"
        trigger="click"
        position="bottom-right"
      >
        <template #target>
          <button
            class="redraw-notes__how-it-works"
            :class="{
              'redraw-notes__how-it-works--standard-artwork': isStandardArtwork
            }"
            type="button"
            @click="openRedrawInformationModal"
          >
            How it works
          </button>
        </template>
        <template #content>
          <RedrawInformation :is-mobile="false" @close="closeRedrawInformationTooltip" />
        </template>
      </Tooltip>
    </label>
    <textarea
      :id="`redraw-note-${index}`"
      ref="textareaRef"
      v-model="note"
      class="redraw-notes__textarea"
      :maxlength="REDRAW_NOTES_MAX_LENGTH"
      placeholder="e.g. Keep the white inside of the text. Change the letters of the logo to orange."
      @input="saveNote"
    ></textarea>
    <label
      v-if="showDesignServicesDeliveryBuffer"
      class="redraw-notes__note redraw-notes__note--delivery"
    >
      Design Services add 1 day to delivery.
    </label>
  </div>
  <CommonModal
    v-if="isMobile && isOpenRedrawInformationModal"
    ref="redrawInformationModal"
    compact
    hide-close-button
    bottom-aligned
    @on-close="closeRedrawInformationModal"
  >
    <RedrawInformation is-mobile @close="closeRedrawInformationModal" />
  </CommonModal>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { EventBus, Events } from "jiffy-store";
import { isMediumDeviceAndUp } from "common/utils/browser";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import Tooltip from "common/vue/components/Tooltip.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import CommonModal from "common/vue/components/CommonModal.vue";
import debounce from "lodash.debounce";
import CustomNotes from "./ArtworkQualityConfigPanel/components/CustomNotes.vue";
import RedrawInformation from "./Modals/RedrawInformation.vue";
import { useDtfStore } from "./store";

export default {
  name: "RedrawNotes",
  components: {
    RedrawInformation,
    Tooltip,
    CommonModal,
    CustomNotes
  },
  props: {
    index: {
      type: Number,
      default: 0
    },
    enhancementNote: {
      type: String,
      default: undefined
    },
    isMultiUpload: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    onLearnMore: {
      type: Function,
      default: null
    }
  },
  expose: ["focusTextarea"],
  setup() {
    return { REDRAW_NOTES_MAX_LENGTH };
  },
  data() {
    return {
      note: "",
      isOpenRedrawInformationModal: false,
      isMobile: !isMediumDeviceAndUp(),
      pricingRefreshTimeout: null,
      previousNoteValue: ""
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "isStandardArtwork",
      "experiments",
      "enableAbAiMonetization",
      "isJiffyMade",
      "showDesignServicesDeliveryBuffer"
    ])
  },
  created() {
    this.debouncedUpdatePdpDates = debounce(() => {
      this.updatePdpDates();
    }, 500);

    this.debouncedSubmitNoteChangedEvent = debounce(noteValue => {
      submitDtfEvent("dtf_need_artist_edits_note_changed", {
        page: "pdp",
        artwork_index: this.index,
        note_value: noteValue,
        note_length: noteValue.length
      });
    }, 500);
  },
  mounted() {
    this.note = this.enhancementNote ?? "";
    this.previousNoteValue = this.enhancementNote ?? "";

    this.handleItemsAdded = () => {
      this.note = "";
    };

    EventBus.on(Events.ITEMS_ADDED_TO_CART, this.handleItemsAdded);
  },
  beforeUnmount() {
    EventBus.removeEventListener(Events.ITEMS_ADDED_TO_CART, this.handleItemsAdded);
    if (this.debouncedUpdatePdpDates) {
      this.debouncedUpdatePdpDates.cancel();
    }
    if (this.debouncedSubmitNoteChangedEvent) {
      this.debouncedSubmitNoteChangedEvent.flush();
      this.debouncedSubmitNoteChangedEvent.cancel();
    }
    clearTimeout(this.pricingRefreshTimeout);
  },
  methods: {
    ...mapActions(useDtfStore, ["updateArtworkValues", "getArtworksPricing", "updatePdpDates"]),
    saveNote(e) {
      const noteValue = (e.target.value ?? "").trim();
      this.updateArtworkValues({
        index: this.index,
        enhancementNote: noteValue
      });

      if (noteValue !== this.previousNoteValue) {
        this.debouncedSubmitNoteChangedEvent(noteValue);
      }

      if (this.showDesignServicesDeliveryBuffer) {
        const hadContent = !!this.previousNoteValue;
        const hasContent = !!noteValue;

        if (hasContent || (hadContent && !hasContent)) {
          this.debouncedUpdatePdpDates();
        }
      }

      this.previousNoteValue = noteValue;

      // Debounce pricing refresh to avoid excessive API calls
      clearTimeout(this.pricingRefreshTimeout);
      this.pricingRefreshTimeout = setTimeout(() => {
        this.getArtworksPricing();
      }, 500);
    },
    openRedrawInformationModal() {
      submitDtfEvent("redraw_information_modal_opened", {
        page: "pdp"
      });
      if (this.isMobile) {
        this.isOpenRedrawInformationModal = true;
      }
    },
    closeRedrawInformationModal() {
      this.$refs.redrawInformationModal.close();
      this.isOpenRedrawInformationModal = false;
    },
    closeRedrawInformationTooltip() {
      this.$refs.redrawInformationTooltip.hideTooltip();
    },
    focusTextarea() {
      if (this.$refs.textareaRef) {
        this.$refs.textareaRef.focus();
        this.$refs.textareaRef.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.redraw-notes {
  $this: &;

  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 120px;

  &:where(:not(#{&}--standard-artwork)) {
    padding-top: 20px;
  }

  &--multi-upload {
    padding-top: 5px;
    margin-bottom: 20px;
  }

  &__how-it-works {
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;
    text-decoration: underline;
    flex-shrink: 0;
    border: none;
    outline: none;
    background: none;
    color: $primary-600;

    &--standard-artwork {
      color: $primary-light;
    }
  }

  &__label-text {
    display: flex;
    flex-wrap: wrap;
    width: 75%;
  }

  &__note {
    font-size: 11px;

    &--delivery {
      font-size: 12px;
    }
  }

  &__label {
    font-family: $font-family-secondary;
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;
    margin-bottom: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__textarea {
    padding: 8px;
    font-size: 16px;
    width: 100%;
    height: 100px;
    box-sizing: border-box;
    resize: none;
    border: 1px solid $crafterpay-gray-modern-400;
    border-radius: 8px;
  }

  &__icon {
    position: absolute;
    height: 15px;
    width: 15px;
    bottom: 10px;
    right: 10px;
  }

  &__indicator {
    position: absolute;
    bottom: 6px;
    right: 10px;
    display: flex;
    align-items: center;
    margin-top: 4px;
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    color: $crafterpay-gray-modern-600;
  }

  &__indicator-icon {
    height: 10px;
    width: 15px;
    color: green;
    margin-right: 3px;
  }
}
</style>
